/**
 * @fileoverview FAQテンプレートカードコンポーネント
 * @description よくある質問のテンプレートを表示
 */
import { useState, useEffect } from 'react'
import {
  FileText,
  MapPin,
  ClipboardList,
  Plane,
  GraduationCap,
  BarChart3,
  Ban,
  Moon,
  Eye,
  CheckSquare,
  Cloud,
  Radio,
  Scale,
  type LucideIcon,
} from 'lucide-react'
import type { PromptTemplate } from '@karosorabei/shared'

import { cn } from '@/lib/utils'
import { CharacterAvatar } from '@/components/chat/character-avatar'

// ============================================================================
// アイコンマッピング
// ============================================================================

/**
 * テンプレートIDに対応するLucideアイコンのマッピング
 * @description P1デザイン準拠のアイコン設定
 */
const TEMPLATE_ICONS: Record<string, LucideIcon> = {
  // 基本規制（basics）
  'registration-required': FileText,
  'remote-id': Radio,
  'weight-limit': Scale,
  // 許可申請（permit）
  'permission-required': CheckSquare,
  'national-license': GraduationCap,
  'license-difference': BarChart3,
  'level4-flight': Plane,
  // 飛行エリア（area）
  'did-area': MapPin,
  'no-fly-zone': Ban,
  // 飛行方法（method）
  'night-flight': Moon,
  'bvlos-flight': Eye,
  // 安全対策（safety）
  'pre-flight-check': ClipboardList,
  'weather-check': Cloud,
}

/**
 * テンプレートIDからアイコンを取得
 * @param templateId - テンプレートID
 * @returns 対応するLucideアイコンコンポーネント
 */
function getTemplateIcon(templateId: string): LucideIcon {
  return TEMPLATE_ICONS[templateId] || FileText
}

// ============================================================================
// 定数
// ============================================================================

/**
 * 家老空兵衛のウェルカムメッセージ
 * @description チャット初期表示時に表示する挨拶文
 */
export const WELCOME_MESSAGE = `殿、ご機嫌うるわしゅうございます。

わしは大空藩の飛行御用家老、空兵衛と申します。ドローン飛行に関してご不明な点は、何なりとお申し付けくださいませ。`

// ============================================================================
// FAQ表示件数計算用の定数
// ============================================================================

/**
 * モバイル画面でのレイアウト固定要素の高さ定数
 * @description 余裕を持たせた概算値（実機のアドレスバー等も考慮）
 */
const FAQ_LAYOUT_CONSTANTS = {
  /** ヘッダー高さ（px） */
  HEADER_HEIGHT: 60,
  /** 入力エリア高さ（safe-area込み、px） */
  INPUT_HEIGHT: 90,
  /** 上下パディング（p-6 × 2 + 余白、px） */
  PADDING: 56,
  /** アバター + 名前 + gap（px） */
  AVATAR_HEIGHT: 48,
  /** ウェルカムメッセージバブル（px） */
  MESSAGE_BUBBLE_HEIGHT: 130,
  /** FAQセクションヘッダー + mb-3（px） */
  FAQ_HEADER_HEIGHT: 40,
  /** 免責注意書き（px） */
  DISCLAIMER_HEIGHT: 30,
  /** FAQ1件あたりの高さ（2行折り返し考慮、px） */
  FAQ_ITEM_HEIGHT: 56,
  /** 最小表示件数 */
  MIN_FAQ_COUNT: 2,
  /** 最大表示件数 */
  MAX_FAQ_COUNT: 6,
} as const

/**
 * ビューポート高さから表示可能なFAQ件数を計算
 * @param viewportHeight - ビューポート高さ（px）
 * @returns 表示すべきFAQ件数
 */
export function calculateFaqCount(viewportHeight: number): number {
  const {
    HEADER_HEIGHT,
    INPUT_HEIGHT,
    PADDING,
    AVATAR_HEIGHT,
    MESSAGE_BUBBLE_HEIGHT,
    FAQ_HEADER_HEIGHT,
    DISCLAIMER_HEIGHT,
    FAQ_ITEM_HEIGHT,
    MIN_FAQ_COUNT,
    MAX_FAQ_COUNT,
  } = FAQ_LAYOUT_CONSTANTS

  // 異常値のガード: 負の値、ゼロ、NaNの場合は最小件数を返す
  if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) {
    return MIN_FAQ_COUNT
  }

  // 固定要素の合計高さ
  const fixedHeight =
    HEADER_HEIGHT +
    INPUT_HEIGHT +
    PADDING +
    AVATAR_HEIGHT +
    MESSAGE_BUBBLE_HEIGHT +
    FAQ_HEADER_HEIGHT +
    DISCLAIMER_HEIGHT

  // FAQ表示に使える残り高さ
  const availableHeight = viewportHeight - fixedHeight

  // 表示可能な件数を計算
  const faqCount = Math.floor(availableHeight / FAQ_ITEM_HEIGHT)

  // 最小・最大の範囲内に収める
  return Math.max(MIN_FAQ_COUNT, Math.min(MAX_FAQ_COUNT, faqCount))
}

// ============================================================================
// コンポーネント
// ============================================================================

/**
 * TemplateCardのprops型
 */
interface TemplateCardProps {
  /** テンプレート情報 */
  template: PromptTemplate
  /** クリック時のコールバック */
  onClick: (template: PromptTemplate) => void
  /** カスタムクラス名 */
  className?: string
}

/**
 * FAQテンプレートカードコンポーネント
 * @description クリック可能なFAQテンプレートカード（prompt全文を表示）
 * @example
 * <TemplateCard template={template} onClick={handleClick} />
 */
export function TemplateCard({ template, onClick, className }: TemplateCardProps) {
  // テンプレートIDに対応するアイコンを取得
  const Icon = getTemplateIcon(template.id)

  return (
    <button
      onClick={() => onClick(template)}
      className={cn(
        // P1デザイン準拠: cornerRadius:12px, padding:[12,16], stroke, 縦並び
        'flex w-full items-center gap-3 rounded-xl bg-white px-4 py-3',
        'border border-[#E5E4E1]',
        'text-left text-sm text-[#1A1918]',
        'transition-all hover:border-[#3D8A5A] hover:shadow-sm',
        'focus:outline-none focus:ring-2 focus:ring-[#3D8A5A] focus:ring-offset-2',
        className
      )}
    >
      <Icon className="h-4 w-4 flex-shrink-0 text-[#3D8A5A]" />
      {/* label → prompt に変更: 質問文全体を表示 */}
      <span>{template.prompt}</span>
    </button>
  )
}

/**
 * テンプレートグリッドコンポーネント
 * @description 複数のテンプレートをグリッド表示
 */
interface TemplateGridProps {
  /** テンプレート一覧 */
  templates: PromptTemplate[]
  /** テンプレートクリック時のコールバック */
  onSelect: (template: PromptTemplate) => void
  /** カスタムクラス名 */
  className?: string
}

export function TemplateGrid({ templates, onSelect, className }: TemplateGridProps) {
  return (
    <div
      className={cn(
        // P1デザイン準拠: 縦並び、gap:8px
        'flex flex-col gap-2',
        className
      )}
    >
      {templates.map((template) => (
        <TemplateCard key={template.id} template={template} onClick={onSelect} />
      ))}
    </div>
  )
}

/**
 * ウェルカムセクションコンポーネント
 * @description 初期状態のチャット画面で表示するウェルカムメッセージとFAQ
 * @remarks
 * 家老空兵衛が殿（ユーザー）に挨拶し、よくある質問を提示する。
 * メッセージ送信後はこのセクションは非表示になる。
 */
interface WelcomeSectionProps {
  /** テンプレート一覧 */
  templates: PromptTemplate[]
  /** テンプレート選択時のコールバック */
  onSelectTemplate: (template: PromptTemplate) => void
  /** カスタムクラス名 */
  className?: string
}

export function WelcomeSection({ templates, onSelectTemplate, className }: WelcomeSectionProps) {
  // 動的にFAQ表示件数を計算（モバイル画面用）
  const [faqCount, setFaqCount] = useState<number>(FAQ_LAYOUT_CONSTANTS.MAX_FAQ_COUNT)

  useEffect(() => {
    /**
     * ビューポート高さからFAQ表示件数を更新
     * @description 画面回転やリサイズ時に再計算
     */
    const updateFaqCount = () => {
      const viewportHeight = window.innerHeight
      const calculatedCount = calculateFaqCount(viewportHeight)
      setFaqCount(calculatedCount)
    }

    // 初回実行
    updateFaqCount()

    // 画面回転・リサイズ時に再計算（モバイルの画面回転対応）
    window.addEventListener('resize', updateFaqCount)

    // クリーンアップ
    return () => window.removeEventListener('resize', updateFaqCount)
  }, [])

  // 計算された件数でテンプレートを切り取り
  const displayTemplates = templates.slice(0, faqCount)

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {/* 家老のウェルカムメッセージバブル */}
      <div className="flex flex-col items-start gap-2">
        {/* 家老アバター（CharacterAvatarを使用） */}
        <CharacterAvatar character="sorabei" size="md" showName />

        {/* メッセージバブル */}
        <div className="max-w-[280px] rounded-2xl bg-white px-4 py-4">
          <p className="whitespace-pre-line text-sm leading-[1.6] text-[#1A1918]">
            {WELCOME_MESSAGE}
          </p>
        </div>
      </div>

      {/* FAQセクション */}
      <div className="w-full pt-2">
        <p className="mb-3 text-[13px] font-semibold text-[#9C9B99]">殿からよくいただくご質問</p>
        <TemplateGrid templates={displayTemplates} onSelect={onSelectTemplate} />
      </div>
    </div>
  )
}
